import { CommonModule } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    HostBinding,
    inject,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Renderer2
} from '@angular/core';
import { App } from '@df/core/app';
import { DfDomModule } from '@df/dom/df-dom.module';
import { ElementHeightService } from '@df/dom/element-height/element-height.service';
import { TimeoutService } from '@df/ng/timeout.service';
import { BlockStickyService } from '@df/ui/block-sticky.service';
import { Subscription } from 'rxjs';
import { StickyFreeService } from './sticky-free.service';
import { ScrollService } from '@df/util/service/scroll.service';

let uid = 0;

@Component({
    standalone: true,
    imports: [CommonModule, DfDomModule],
    selector: 'sticky-free',
    templateUrl: './sticky-free.html',
    styleUrl: './sticky-free.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class StickyFreeComponent implements OnInit, OnDestroy, OnChanges {
    private readonly _uid = `stickyfree${uid++}`;

    @Input()
    order?: number;

    @Input()
    stickyOff?: boolean;

    @Input()
    stickyId?: string;

    @Input()
    noShadow = false;

    get uid() {
        return this.stickyId || this._uid;
    }

    private isStickyOn?: boolean;

    private initiated = false;

    @Input()
    @HostBinding('class.isRangeSetter')
    isRangeSetter = false;

    /**
     * If bottom shadow class is applied
     */
    private isSticky = false;

    private unregister?: () => void;
    private subscription?: Subscription;
    private initialSubscription?: Subscription;
    private scrollSubscription?: Subscription;
    private subscriptions = new Subscription();
    private readonly renderer2 = inject(Renderer2);
    private readonly elementRef = inject(ElementRef);
    private readonly stickyFreeService = inject(StickyFreeService);
    private readonly elementHeightService = inject(ElementHeightService);
    private readonly scrollService = inject(ScrollService);
    private readonly timeoutService = inject(TimeoutService);
    private readonly blockStickyService = inject(BlockStickyService);
    private readonly app = inject(App);

    ngOnInit() {
        if (this.app.isPlatformBrowser) {
            this.applySticky();
            this.initialSubscription = this.elementHeightService.getState(this.uid).subscribe(value => {
                if (value !== undefined) {
                    this.renderer2.setStyle(this.elementRef.nativeElement, 'height', this.stickyFreeService.toVar(this.uid));
                    this.renderer2.addClass(this.elementRef.nativeElement, 'is-ready');
                    const isSafari = true || /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

                    // its only needed for safari below 15.4, we can drop it in about 1y
                    if (isSafari) {
                        this.elementRef.nativeElement.style.setProperty(
                            '--in-app-shift',
                            `${2 * this.elementRef.nativeElement.getBoundingClientRect().top + this.scrollService.getScrollTop()}px`
                        );
                    }
                    this.initialSubscription?.unsubscribe();
                    this.initialSubscription = undefined;
                }
            });

            this.scrollSubscription = this.scrollService.scroll$.subscribe(
                this.timeoutService.throttle(
                    () => {
                        if (this.isStickyOn) {
                            const el = this.elementRef.nativeElement;
                            const top = el.getBoundingClientRect().top;
                            this.updateIsStickyClass(top < 0);
                            if (this.isRangeSetter) {
                                const scrollTop = this.scrollService.getScrollTop();
                                this.stickyFreeService.setStickyRange(`${top + scrollTop}px`);
                            }
                        }
                    },
                    500,
                    { leading: true, trailing: true }
                )
            );

            const isFirefox = /^((?!chrome|android).)*firefox/i.test(navigator.userAgent);
            if (isFirefox) {
                this.subscriptions.add(
                    this.scrollService.isBlocked$.subscribe(isBlocked => {
                        if (isBlocked) {
                            this.renderer2.addClass(this.elementRef.nativeElement, 'isFirefoxBlockScroll');
                        } else {
                            this.renderer2.removeClass(this.elementRef.nativeElement, 'isFirefoxBlockScroll');
                        }
                    })
                );
            }
            this.subscriptions.add(this.blockStickyService.state$.subscribe(() => this.initiated && this.applySticky()));
            this.initiated = true;
        }
    }

    ngOnChanges(changes) {
        if (this.initiated) {
            if (changes.stickyOff) {
                this.applySticky();
            }
        }

        if (changes.isRangeSetter && !this.isRangeSetter) {
            this.stickyFreeService.setStickyRange(undefined);
        }
    }

    ngOnDestroy() {
        this.unsetStickyOn();
        this.initialSubscription?.unsubscribe();
        this.scrollSubscription?.unsubscribe();
        if (this.isRangeSetter) {
            this.stickyFreeService.setStickyRange(undefined);
        }
        this.subscriptions.unsubscribe();
    }

    private applySticky() {
        this.stickyOff || this.blockStickyService.state$.value ? this.unsetStickyOn() : this.setStickyOn();
    }

    private setStickyOn() {
        if (this.isStickyOn) {
            return;
        }
        this.isStickyOn = true;
        this.renderer2.removeClass(this.elementRef.nativeElement, 'isOff');
        if (this.order !== undefined) {
            this.subscription = this.stickyFreeService.entriesChange.subscribe(() => {
                this.elementRef.nativeElement.style.setProperty('--sticky-offset', this.stickyFreeService.getOffsets(this.order || 0));
            });
            this.unregister = this.stickyFreeService.add({
                uid: this.uid,
                order: this.order
            });
        }
    }

    private unsetStickyOn() {
        if (this.isStickyOn === false) {
            return;
        }
        this.isStickyOn = false;
        this.renderer2.addClass(this.elementRef.nativeElement, 'isOff');
        if (this.unregister) {
            this.unregister();
            this.unregister = undefined;
        }
        if (this.subscription) {
            this.subscription.unsubscribe();
            this.subscription = undefined;
        }
    }

    /**
     * Updates bottom shadow class
     */
    private updateIsStickyClass(isSticky = this.isSticky) {
        if (this.isSticky !== isSticky) {
            if (isSticky) {
                this.renderer2.addClass(this.elementRef.nativeElement, 'isSticky');
            } else {
                this.renderer2.removeClass(this.elementRef.nativeElement, 'isSticky');
            }
            this.isSticky = isSticky;
        }
    }
}
